import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Dices, X as XIcon, Tv, Check } from "lucide-react";

const COUNTDOWN_SECONDS = 3;
const BUMPER_SECONDS = 5;

export default function WheelspinReveal({ fixture, canRoll, canBroadcast, onRolled, side, isPrivileged, onAdminReady }) {
  const alreadyRolled = !!fixture?.wheelspin_map;
  const readyState = fixture?.ready || {};
  const bothReady = !!(readyState.home && readyState.away);

  const [maps, setMaps] = useState([]);
  const [broadcast, setBroadcast] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | bumper | countdown | spinning | locked
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [bumperLeft, setBumperLeft] = useState(BUMPER_SECONDS);
  const [current, setCurrent] = useState(fixture?.wheelspin_map || null);
  const [locked, setLocked] = useState(alreadyRolled);
  const tickerRef = useRef(null);
  const stopRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get("/wheelspin/maps").then((r) => setMaps(r.data.maps || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (fixture?.wheelspin_map) {
      setCurrent(fixture.wheelspin_map);
      setLocked(true);
      setPhase("locked");
    }
  }, [fixture?.wheelspin_map]);

  const cleanup = () => {
    if (tickerRef.current) clearInterval(tickerRef.current);
    if (stopRef.current) clearTimeout(stopRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    tickerRef.current = stopRef.current = timerRef.current = null;
  };
  useEffect(() => () => cleanup(), []);

  const closeBroadcast = () => {
    cleanup();
    setBroadcast(false);
    if (phase !== "locked") setPhase("idle");
  };

  const openBroadcast = () => {
    if (locked || !bothReady) return;
    setBroadcast(true);
    setPhase("bumper");
    setBumperLeft(BUMPER_SECONDS);
    let b = BUMPER_SECONDS;
    timerRef.current = setInterval(() => {
      b -= 1;
      setBumperLeft(b);
      if (b <= 0) {
        clearInterval(timerRef.current); timerRef.current = null;
        setPhase("countdown");
        setCountdown(COUNTDOWN_SECONDS);
        let c = COUNTDOWN_SECONDS;
        timerRef.current = setInterval(() => {
          c -= 1;
          setCountdown(c);
          if (c <= 0) {
            clearInterval(timerRef.current); timerRef.current = null;
            startSpin();
          }
        }, 1000);
      }
    }, 1000);
  };

  const skipBumper = () => {
    if (phase !== "bumper") return;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setPhase("countdown");
    setCountdown(COUNTDOWN_SECONDS);
    let c = COUNTDOWN_SECONDS;
    timerRef.current = setInterval(() => {
      c -= 1;
      setCountdown(c);
      if (c <= 0) {
        clearInterval(timerRef.current); timerRef.current = null;
        startSpin();
      }
    }, 1000);
  };

  const startSpin = async () => {
    if (locked || maps.length === 0) return;
    setPhase("spinning");
    tickerRef.current = setInterval(() => {
      setCurrent(maps[Math.floor(Math.random() * maps.length)]);
    }, 55);

    try {
      const { data } = await api.post(`/fixtures/${fixture.id}/wheelspin`);
      const finalPick = data.wheelspin_map;
      stopRef.current = setTimeout(() => {
        if (tickerRef.current) clearInterval(tickerRef.current);
        let slow = 90;
        const tick = () => {
          setCurrent(maps[Math.floor(Math.random() * maps.length)]);
          slow += 45;
          if (slow < 620) {
            stopRef.current = setTimeout(tick, slow);
          } else {
            setCurrent(finalPick);
            setLocked(true);
            setPhase("locked");
            onRolled?.(finalPick);
          }
        };
        stopRef.current = setTimeout(tick, slow);
      }, 2200);
    } catch (e) {
      cleanup();
      setPhase("idle");
      setBroadcast(false);
      toast.error(e.response?.data?.detail || "Roll failed");
    }
  };

  const gateMsg = !bothReady && !locked
    ? "Awaiting ready · both teams must lock in first"
    : null;

  // Iter36 · Pre-warm countdown: 30 minutes before kickoff, show reps a
  // "Wheelspin opens in Xm" heads-up so nobody is caught off guard. The
  // wheel still only unlocks once both reps have marked ready.
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (locked) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [locked]);
  const kickoffMs = fixture?.scheduled_at ? new Date(fixture.scheduled_at).getTime() : null;
  const prewarmMsLeft = kickoffMs ? kickoffMs - nowTick : null;
  const inPrewarm = prewarmMsLeft !== null && prewarmMsLeft > 0 && prewarmMsLeft <= 30 * 60_000;
  const prewarmLabel = (() => {
    if (!inPrewarm) return null;
    const mins = Math.floor(prewarmMsLeft / 60_000);
    const secs = Math.floor((prewarmMsLeft % 60_000) / 1000);
    return mins > 0 ? `Wheelspin opens in ${mins}m ${String(secs).padStart(2, "0")}s` : `Wheelspin opens in ${secs}s`;
  })();

  return (
    <>
      <div
        data-testid={`wheelspin-${fixture?.id}`}
        className={`border ${locked ? "border-[#F4D03F]/60 bg-[#F4D03F]/5" : "border-[#B084FF]/50 bg-[#B084FF]/5"} p-3 mt-2`}
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Dices className={`w-4 h-4 shrink-0 ${locked ? "text-[#F4D03F]" : "text-[#B084FF]"}`} />
            <div className="min-w-0">
              <div className="text-[9px] font-mono uppercase tracking-widest text-gray-500">
                {locked ? "Wheelspin locked" : gateMsg || "Wheelspin — hidden until rolled"}
              </div>
              <div
                data-testid={`wheelspin-current-${fixture?.id}`}
                className={`font-heading font-black uppercase tracking-tight text-lg sm:text-xl truncate ${
                  locked ? "text-[#F4D03F]" : "text-gray-500"
                }`}
              >
                {locked ? current : "??? ??? ???"}
              </div>
            </div>
          </div>
          {!locked && canRoll && !bothReady && side && !isPrivileged && (
            <button
              onClick={async () => {
                try {
                  const { data } = await api.post(`/fixtures/${fixture.id}/ready`, null, { params: side ? { side } : {} });
                  if (data?.coin_toss?.auto) {
                    const winnerTag = data.coin_toss.winner === "home"
                      ? (fixture.home_team_tag || "HOME")
                      : (fixture.away_team_tag || "AWAY");
                    toast.success(`Both ready — coin flipped, ${winnerTag} won the toss!`);
                  } else {
                    toast.success("Marked ready.");
                  }
                  if (typeof onRolled === "function") onRolled();
                } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }}
              data-testid={`wheelspin-ready-${fixture?.id}`}
              className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest text-xs font-bold px-3 py-2 flex items-center gap-2"
            >
              <Check className="w-3.5 h-3.5" /> Ready in game
            </button>
          )}
          {/* Iter78/84 · Admin / official override — always available to privileged
              users (even when they also rep one of the teams), per the rule that
              admins/officials are the ONLY roles allowed to ready any team. */}
          {!locked && isPrivileged && !bothReady && (
            <div className="flex items-center gap-2 flex-wrap" data-testid={`wheelspin-admin-ready-${fixture?.id}`}>
              <span className="text-[9px] font-mono uppercase tracking-widest text-[#F4D03F] border border-[#F4D03F]/40 bg-[#F4D03F]/10 px-2 py-2">
                Admin override
              </span>
              <button
                onClick={() => onAdminReady && onAdminReady("home")}
                disabled={!!(fixture.ready || {}).home}
                data-testid={`wheelspin-admin-ready-home-${fixture?.id}`}
                className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest text-xs font-bold px-3 py-2 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Check className="w-3.5 h-3.5" /> Ready HOME
              </button>
              <button
                onClick={() => onAdminReady && onAdminReady("away")}
                disabled={!!(fixture.ready || {}).away}
                data-testid={`wheelspin-admin-ready-away-${fixture?.id}`}
                className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest text-xs font-bold px-3 py-2 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Check className="w-3.5 h-3.5" /> Ready AWAY
              </button>
            </div>
          )}
          {!locked && canBroadcast && (
            <button
              onClick={openBroadcast}
              disabled={!bothReady || maps.length === 0 || !fixture?.scheduled_at}
              data-testid={`wheelspin-roll-${fixture?.id}`}
              title={!fixture?.scheduled_at ? "Reps must agree a kickoff time first" : (!bothReady ? "Both teams must ready up first" : "Broadcast reveal")}
              className="rounded-none bg-[#B084FF] hover:bg-purple-400 text-black uppercase tracking-widest text-xs font-bold px-3 py-2 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Tv className="w-3.5 h-3.5" /> Broadcast reveal
            </button>
          )}
          {!locked && inPrewarm && !bothReady && (
            <span
              data-testid={`wheelspin-prewarm-${fixture?.id}`}
              className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F] border border-[#F4D03F]/40 bg-[#F4D03F]/10 px-3 py-2"
              title="Reps: mark ready to unlock the wheel"
            >
              {prewarmLabel}
            </span>
          )}
          {!locked && !canBroadcast && bothReady && (
            <span
              data-testid={`wheelspin-official-gate-${fixture?.id}`}
              className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F] border border-[#F4D03F]/40 px-3 py-2"
              title="Only a rep of either team, a League Official, or an Admin can roll the map wheel"
            >
              Awaiting rep spin
            </span>
          )}
          {locked && (
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F]">
              {fixture?.wheelspin_rolled_at ? "Rolled " + new Date(fixture.wheelspin_rolled_at).toLocaleString() : "Locked"}
            </span>
          )}
        </div>
      </div>

      {broadcast && (
        <div
          data-testid="wheelspin-broadcast"
          className="fixed inset-0 z-[60] bg-black flex flex-col items-center justify-center p-6"
        >
          {/* neon backdrop */}
          <div className="pointer-events-none absolute inset-0" style={{
            background: "radial-gradient(ellipse at center, rgba(176,132,255,0.25), transparent 60%), radial-gradient(ellipse at bottom, rgba(244,208,63,0.15), transparent 60%)",
          }} />
          <button
            onClick={closeBroadcast}
            data-testid="wheelspin-broadcast-close"
            className="absolute top-6 right-6 text-white/60 hover:text-white flex items-center gap-1 text-xs font-mono uppercase tracking-widest z-10"
          >
            <XIcon className="w-4 h-4" /> Close
          </button>
          <div className="text-[#B084FF] font-mono tracking-[0.3em] text-xs mb-4 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> / WHEELSPIN LIVE
          </div>
          <div className="font-heading font-black uppercase tracking-tighter text-2xl sm:text-4xl text-white mb-8 text-center">
            {fixture?.home_team_tag || "HOME"}
            <span className="text-gray-600 mx-3">vs</span>
            {fixture?.away_team_tag || "AWAY"}
          </div>

          {phase === "bumper" && (
            <div
              data-testid="wheelspin-broadcast-bumper"
              className="text-center animate-in fade-in zoom-in duration-500 relative"
            >
              <div className="text-[#B084FF] font-mono tracking-[0.4em] text-xs mb-6 uppercase animate-pulse">
                Coming Up · Map Reveal
              </div>
              <div
                className="font-heading font-black leading-none uppercase tracking-tighter text-white"
                style={{ fontSize: "clamp(3rem, 10vw, 9rem)", textShadow: "0 0 60px rgba(176,132,255,0.7)" }}
              >
                Wheelspin
              </div>
              <div className="mt-4 text-[#F4D03F] font-heading font-black text-2xl sm:text-4xl uppercase tracking-tight">
                Live in {bumperLeft}s
              </div>
              <div className="mt-8 h-1 w-64 mx-auto bg-[#232330] overflow-hidden">
                <div
                  className="h-full bg-[#B084FF]"
                  style={{
                    width: `${((BUMPER_SECONDS - bumperLeft) / BUMPER_SECONDS) * 100}%`,
                    transition: "width 1s linear",
                    boxShadow: "0 0 20px rgba(176,132,255,0.8)",
                  }}
                />
              </div>
              <button
                onClick={skipBumper}
                data-testid="wheelspin-broadcast-skip"
                className="mt-10 text-[10px] font-mono uppercase tracking-widest text-white/50 hover:text-white"
              >
                Skip intro →
              </button>
            </div>
          )}

          {phase === "countdown" && (
            <div
              data-testid="wheelspin-broadcast-countdown"
              className="font-heading font-black leading-none text-white"
              style={{ fontSize: "clamp(8rem, 22vw, 18rem)", textShadow: "0 0 60px rgba(176,132,255,0.7)" }}
            >
              {countdown > 0 ? countdown : "SPIN"}
            </div>
          )}

          {phase === "spinning" && (
            <div
              data-testid="wheelspin-broadcast-spinning"
              className="font-heading font-black uppercase tracking-tight text-center text-[#B084FF] animate-pulse"
              style={{ fontSize: "clamp(2.5rem, 7vw, 6rem)", textShadow: "0 0 40px rgba(176,132,255,0.6)" }}
            >
              {current || "…"}
            </div>
          )}

          {phase === "locked" && (
            <div className="text-center animate-in fade-in zoom-in duration-500">
              <div className="text-[#F4D03F] font-mono tracking-[0.3em] text-xs mb-4 flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4" /> LOCKED IN
              </div>
              <div
                data-testid="wheelspin-broadcast-final"
                className="font-heading font-black uppercase tracking-tighter text-[#F4D03F]"
                style={{ fontSize: "clamp(3rem, 9vw, 8rem)", textShadow: "0 0 60px rgba(244,208,63,0.8)" }}
              >
                {current}
              </div>
              <div className="text-white/70 font-mono uppercase tracking-widest text-xs mt-8">
                Cut to game — kickoff imminent.
              </div>
              <button
                onClick={closeBroadcast}
                className="mt-8 rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest text-xs font-bold px-5 py-2.5"
              >
                Close broadcast
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
